import React, { useCallback, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getOverview, type Overview, type OverviewRange } from './adminApi';
import { useAdminAccess } from './adminAccess';
import { usePolling } from './usePolling';
import { formatCount, formatCredits, formatPercent, formatPlatform, formatUtc } from './format';
import DailyPlayersChart, { type DayPoint } from './DailyPlayersChart';
import { SlowBanner, Unavailable } from './PanelState';

const RANGES: Array<{ value: OverviewRange; label: string }> = [
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
];

const PLATFORM_ORDER = ['web', 'ios', 'android'];

/** The server lists only days with play; the chart shows every day of the range. */
export function fillDays(overview: Overview): DayPoint[] {
  const { days, to } = overview.range;
  if (!days || !to) return overview.dau;
  const byDay = new Map(overview.dau.map(point => [point.day, point.players]));
  const end = Date.parse(`${to}T00:00:00Z`);
  if (!Number.isFinite(end)) return overview.dau;
  return Array.from({ length: days }, (_, index) => {
    const day = new Date(end - (days - 1 - index) * 86400000).toISOString().slice(0, 10);
    return { day, players: byDay.get(day) ?? 0 };
  });
}

function platformMix(platforms: Record<string, number>) {
  const keys = Object.keys(platforms);
  if (keys.length === 0) return '—';
  const ordered = [...PLATFORM_ORDER, ...keys.filter(key => !PLATFORM_ORDER.includes(key)).sort()];
  return ordered.map(key => `${formatPlatform(key)} ${formatPercent(platforms[key] ?? 0)}`).join(' · ');
}

const Tile: React.FC<{ label: string; value: string; detail?: string }> = ({ label, value, detail }) => (
  <div className="admin-tile">
    <dt>{label}</dt>
    <dd><span>{value}</span>{detail && <span className="admin-sub">{detail}</span>}</dd>
  </div>
);

/** Engagement for the last 7/30/90 days (spec US2). Bots are never counted; amounts are play credits. */
const OverviewPanel: React.FC = () => {
  const { token } = useAuth();
  const report = useAdminAccess();
  const [range, setRange] = useState<OverviewRange>('7d');
  const fetcher = useCallback((signal: AbortSignal) => getOverview(token, range, signal), [token, range]);
  const { data, error, refresh } = usePolling<Overview>(fetcher, null, { onError: report });

  const cohort = data?.retention.cohort;
  // A rate over nobody is not 0%: show a dash until there is a cohort to measure.
  const noCohort = cohort === 0;
  const cohortDetail = cohort === undefined ? undefined
    : noCohort ? 'No new players to measure yet'
    : `of ${formatCount(cohort)} new ${cohort === 1 ? 'player' : 'players'}`;
  const days = data ? fillDays(data) : [];
  const anyPlayers = days.some(point => point.players > 0);

  return (
    <div className="admin-overview">
      <div className="admin-toolbar">
        <div className="admin-segmented" role="group" aria-label="Period">
          {RANGES.map(option => (
            <button
              key={option.value}
              type="button"
              aria-pressed={range === option.value}
              onClick={() => setRange(option.value)}
            >{option.label}</button>
          ))}
        </div>
        {data?.generatedAt && <span className="admin-muted">Figures as of {formatUtc(data.generatedAt)}</span>}
      </div>

      <SlowBanner error={error} />
      {error && error.kind !== 'slow' && error.kind !== 'signin' && error.kind !== 'forbidden' && (
        <Unavailable message="Analytics unavailable" onRetry={refresh} />
      )}
      {error?.kind === 'slow' && !data && <Unavailable message="Analytics paused" onRetry={refresh} />}
      {!data && !error && <p className="admin-muted">Loading overview…</p>}

      {data && !error && (
        <>
          <dl className="admin-tile-grid">
            <Tile label="Weekly active" value={formatCount(data.wau)} />
            <Tile label="Monthly active" value={formatCount(data.mau)} />
            <Tile label="New players" value={formatCount(data.newPlayers)} />
            <Tile label="Next-day return" value={noCohort ? '—' : formatPercent(data.retention.d1)} detail={cohortDetail} />
            <Tile label="7-day return" value={noCohort ? '—' : formatPercent(data.retention.d7)} detail={cohortDetail} />
            <Tile label="Rounds" value={formatCount(data.rounds)} />
            <Tile label="Credits wagered" value={formatCredits(data.wagered)} detail="play credits" />
            <Tile label="Credits paid out" value={formatCredits(data.paidOut)} detail="play credits" />
            <Tile
              label="Return to player"
              value={formatPercent(data.realisedRtp)}
              detail={data.expectedRtp === undefined ? 'realised' : `realised · expected ${formatPercent(data.expectedRtp)}`}
            />
            <Tile label="Auto-cashout share" value={formatPercent(data.autoCashoutShare)} />
            <Tile label="Platform mix" value={platformMix(data.platforms)} detail="share of sessions" />
          </dl>

          <section className="admin-chart-card" aria-label="Daily active players">
            <div className="panel-heading"><p className="section-label">Daily active players</p></div>
            {anyPlayers
              ? <DailyPlayersChart days={days} />
              : <p className="admin-empty">No players in this period yet</p>}
          </section>

          <div className="admin-footnotes">
            <p>Days in UTC (02:00 SAST)</p>
            <p>Includes solo runs. Bots excluded. Amounts are play credits.</p>
          </div>
        </>
      )}
    </div>
  );
};

export default OverviewPanel;
